import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DollarSign, Heart, Home, BookOpen, Search, Phone,
  MapPin, ExternalLink, Users, Briefcase, Clock, Star,
  Filter, X, ChevronRight
} from "lucide-react";

/**
 * Local Resource Filter Component
 * Categories: Financial Aid, Respite Care, In-Home Nursing, Grief Counseling
 * Tags: Volunteer-Led or Professional Service
 */

const RESOURCE_CATEGORIES = {
  financial_aid: {
    id: "financial_aid",
    name: "Financial Aid",
    icon: DollarSign,
    color: "bg-green-100 text-green-700",
    description: "Help with medical costs, bills, and financial assistance"
  },
  respite_care: {
    id: "respite_care",
    name: "Respite Care",
    icon: Heart,
    color: "bg-rose-100 text-rose-700",
    description: "Temporary relief for caregivers"
  },
  in_home_nursing: {
    id: "in_home_nursing",
    name: "In-Home Nursing",
    icon: Home,
    color: "bg-blue-100 text-blue-700",
    description: "Professional nursing care at home"
  },
  grief_counseling: {
    id: "grief_counseling",
    name: "Grief Counseling",
    icon: BookOpen,
    color: "bg-purple-100 text-purple-700",
    description: "Support for grief and bereavement"
  }
};

const SERVICE_TYPES = {
  volunteer: { label: "Volunteer-Led", color: "bg-amber-100 text-amber-700", icon: Users },
  professional: { label: "Professional Service", color: "bg-[#E8F4F0] text-[#4A7C6F]", icon: Briefcase }
};

// Sample resources - In production, these would come from API
const SAMPLE_RESOURCES = [
  {
    id: "1",
    name: "Virginia Medicare Assistance Program",
    category: "financial_aid",
    service_type: "professional",
    description: "Helps eligible individuals pay Medicare premiums, deductibles, and coinsurance.",
    phone: "800-552-3402",
    website: "https://www.dss.virginia.gov",
    address: "Richmond, VA (Serves all Virginia)",
    hours: "Mon-Fri 8am-5pm",
    rating: 4.5
  },
  {
    id: "2",
    name: "NOVA Respite Care Volunteers",
    category: "respite_care",
    service_type: "volunteer",
    description: "Trained volunteers provide companionship and short-term care relief for family caregivers.",
    phone: "703-555-0123",
    website: null,
    address: "Fairfax, VA",
    hours: "By appointment",
    rating: 4.8
  },
  {
    id: "3",
    name: "Goodwin Care at Home",
    category: "in_home_nursing",
    service_type: "professional",
    description: "Comprehensive hospice and palliative care services including skilled nursing at home.",
    phone: "800-869-2136",
    website: "https://www.capitalcaring.org",
    address: "Falls Church, VA",
    hours: "24/7",
    rating: 4.7
  },
  {
    id: "4",
    name: "Wendt Center for Loss and Healing",
    category: "grief_counseling",
    service_type: "professional",
    description: "Specialized grief counseling and therapy services for individuals and families.",
    phone: "202-624-0010",
    website: "https://www.wendtcenter.org",
    address: "Washington, DC (Serves Northern VA)",
    hours: "Mon-Sat 9am-8pm",
    rating: 4.9
  },
  {
    id: "5",
    name: "Catholic Charities Financial Assistance",
    category: "financial_aid",
    service_type: "volunteer",
    description: "Emergency financial assistance for medical bills, rent, and utilities.",
    phone: "703-841-3838",
    website: "https://www.ccda.net",
    address: "Arlington, VA",
    hours: "Mon-Fri 9am-4pm",
    rating: 4.3
  },
  {
    id: "6",
    name: "Faith in Action Caregivers",
    category: "respite_care",
    service_type: "volunteer",
    description: "Interfaith volunteer caregivers providing respite, transportation, and companionship.",
    phone: "703-333-4545",
    website: null,
    address: "McLean, VA",
    hours: "Flexible scheduling",
    rating: 4.6
  },
  {
    id: "7",
    name: "Inova VNA Home Health",
    category: "in_home_nursing",
    service_type: "professional",
    description: "Licensed home health aides and skilled nursing services throughout Northern Virginia.",
    phone: "703-750-8800",
    website: "https://www.inova.org/vna",
    address: "Annandale, VA",
    hours: "24/7",
    rating: 4.5
  },
  {
    id: "8",
    name: "GriefShare Support Groups",
    category: "grief_counseling",
    service_type: "volunteer",
    description: "Weekly grief support groups facilitated by trained volunteer counselors at local churches.",
    phone: "703-555-7890",
    website: "https://www.griefshare.org",
    address: "Multiple locations in NOVA",
    hours: "Weekly meetings",
    rating: 4.7
  },
  {
    id: "9",
    name: "Loudoun Free Clinic",
    category: "financial_aid",
    service_type: "professional",
    description: "Free medical care and prescription assistance for uninsured residents.",
    phone: "703-779-5412",
    website: "https://www.loudounfreeclinic.org",
    address: "Leesburg, VA",
    hours: "Mon-Fri by appointment",
    rating: 4.8
  },
  {
    id: "10",
    name: "Hospice Volunteer Companions",
    category: "respite_care",
    service_type: "volunteer",
    description: "Compassionate volunteers provide bedside companionship while caregivers take a break.",
    phone: "703-578-7108",
    website: null,
    address: "Northern Virginia area",
    hours: "Flexible",
    rating: 4.9
  }
];

export default function LocalResourceFilter() {
  const [resources, setResources] = useState(SAMPLE_RESOURCES);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedServiceType, setSelectedServiceType] = useState("all");

  const filteredResources = useMemo(() => {
    return resources.filter(resource => {
      const matchesSearch = searchTerm === "" || 
        resource.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.description.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCategory = selectedCategory === "all" || resource.category === selectedCategory;
      const matchesServiceType = selectedServiceType === "all" || resource.service_type === selectedServiceType;
      
      return matchesSearch && matchesCategory && matchesServiceType;
    });
  }, [resources, searchTerm, selectedCategory, selectedServiceType]);

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedCategory("all");
    setSelectedServiceType("all");
  };

  const hasActiveFilters = searchTerm || selectedCategory !== "all" || selectedServiceType !== "all";

  return (
    <div className="space-y-6" data-testid="local-resource-filter">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#4A7C6F] to-[#6B9B8A] rounded-2xl p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">Northern Virginia Resources</h2>
        <p className="text-white/80">
          Find local support services for financial assistance, respite care, nursing, and grief counseling.
        </p>
      </div>

      {/* Search and Filters */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-4">
          {/* Search Bar */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search resources by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="resource-search"
            />
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap gap-2 mb-4">
            <Button
              variant={selectedCategory === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory("all")}
              className={selectedCategory === "all" ? "bg-[#4A7C6F]" : ""}
            >
              All Categories
            </Button>
            {Object.values(RESOURCE_CATEGORIES).map(cat => {
              const Icon = cat.icon;
              return (
                <Button
                  key={cat.id}
                  variant={selectedCategory === cat.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(cat.id)}
                  className={selectedCategory === cat.id ? "bg-[#4A7C6F]" : ""}
                >
                  <Icon className="w-4 h-4 mr-1" />
                  {cat.name}
                </Button>
              );
            })}
          </div>

          {/* Service Type Filters */}
          <div className="flex flex-wrap gap-2">
            <span className="text-sm text-slate-500 self-center mr-2">Service Type:</span>
            <Button
              variant={selectedServiceType === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedServiceType("all")}
              className={selectedServiceType === "all" ? "bg-slate-700" : ""}
            >
              All
            </Button>
            {Object.entries(SERVICE_TYPES).map(([key, type]) => {
              const Icon = type.icon;
              return (
                <Button
                  key={key}
                  variant={selectedServiceType === key ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedServiceType(key)}
                  className={selectedServiceType === key ? "bg-slate-700" : ""}
                >
                  <Icon className="w-4 h-4 mr-1" />
                  {type.label}
                </Button>
              );
            })}
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="mt-2 text-rose-600 hover:text-rose-700"
            >
              <X className="w-4 h-4 mr-1" />
              Clear all filters
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">
          Showing <strong>{filteredResources.length}</strong> of {resources.length} resources
        </p>
      </div>

      {/* Resource Cards */}
      <div className="grid md:grid-cols-2 gap-4">
        {filteredResources.map(resource => {
          const category = RESOURCE_CATEGORIES[resource.category];
          const serviceType = SERVICE_TYPES[resource.service_type];
          const CategoryIcon = category?.icon || Heart;
          
          return (
            <Card 
              key={resource.id} 
              className="border-0 shadow-md hover:shadow-lg transition-shadow"
              data-testid={`resource-card-${resource.id}`}
            >
              <CardContent className="p-5">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg ${category?.color || 'bg-slate-100'} flex items-center justify-center`}>
                      <CategoryIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800">{resource.name}</h3>
                      <p className="text-sm text-slate-500">{category?.name}</p>
                    </div>
                  </div>
                  <Badge className={serviceType?.color}>
                    {serviceType?.label}
                  </Badge>
                </div>

                {/* Description */}
                <p className="text-sm text-slate-600 mb-4">{resource.description}</p>

                {/* Details */}
                <div className="space-y-2 mb-4">
                  {resource.address && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      {resource.address}
                    </div>
                  )}
                  {resource.hours && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Clock className="w-4 h-4 text-slate-400" />
                      {resource.hours}
                    </div>
                  )}
                  {resource.rating && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                      {resource.rating} rating
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  {resource.phone && (
                    <a href={`tel:${resource.phone}`} className="flex-1">
                      <Button variant="outline" className="w-full gap-2">
                        <Phone className="w-4 h-4" />
                        Call
                      </Button>
                    </a>
                  )}
                  {resource.website && (
                    <a 
                      href={resource.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex-1"
                    >
                      <Button variant="outline" className="w-full gap-2">
                        <ExternalLink className="w-4 h-4" />
                        Website
                      </Button>
                    </a>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* No Results */}
      {filteredResources.length === 0 && (
        <Card className="border-0 shadow-md">
          <CardContent className="py-12 text-center">
            <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-semibold text-slate-700 mb-2">No resources found</h3>
            <p className="text-slate-500 mb-4">
              Try adjusting your search or filters
            </p>
            <Button variant="outline" onClick={clearFilters}>
              Clear filters
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
